import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject, containerless } from 'aurelia-framework';
import { bindable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ValidationController, ValidationControllerFactory, ValidationRules, Validator } from 'aurelia-validation';
import { Models } from 'models/core';
import { ErrorService } from 'services/error-service';
import { MaterialService } from 'services/material-service';
import { MeshSizeService } from 'services/mesh-size-service';
import { NettingColorService } from 'services/netting-color-service';
import { NettingService } from 'services/netting-service';
import { NettingTypeService } from 'services/netting-type-service';
import { NylonTypeService } from 'services/nylon-type-service';
import { PlacementService } from 'services/placement-service';
import { ThreadTypeService } from 'services/thread-type-service';
import { ToastService } from 'services/toast-service';
import { validationRules } from './helper';

@containerless
@autoinject
export class NettingFormInlineNew {
  @bindable
  private netId: number;
  @bindable
  private dimensionClassId: number;
  @bindable
  private netShapeId: number;

  @bindable
  private netHasSameNetting = true;
  async netHasSameNettingChanged() {
    await this.getPlacements();
  }

  @bindable
  private isCombiNet = false;
  async isCombiNetChanged() {
    this.cancel();
  }
  @bindable
  isMorenotNet = true;

  private currentNettingType: Models.NettingType;
  private netting: Models.Netting = new Models.Netting();
  private validCombinations: { [shapeId: number]: Array<number> };

  public placements: Models.Placement[] = [];

  protected nettingStrengthLowerThenRequirements = false;
  protected nettingMissingActualBreakingStrength = false;

  protected validationController: ValidationController;

  constructor(
    private meshSizeService: MeshSizeService,
    private errorService: ErrorService,
    private eventAggregator: EventAggregator,
    private nettingService: NettingService,
    private nettingTypeService: NettingTypeService,
    private placementService: PlacementService,
    private toastService: ToastService,
    protected nettingColorService: NettingColorService,
    protected materialService: MaterialService,
    protected threadTypeService: ThreadTypeService,
    protected nylonTypeService: NylonTypeService,
    protected validationControllerFactory: ValidationControllerFactory,
    protected validator: Validator,
    private t: I18N
  ) {
    this.validationController = validationControllerFactory.createForCurrentScope();
  }

  private applyValidation(isMorenotNet: boolean) {
    validationRules(this.t, isMorenotNet)?.on(this.netting);
  }

  protected async isFormValid() {
    const result = await this.validationController.validate({
      object: this.netting,
    });

    return result.valid;
  }

  trySetRequirementsWarning(strengthRequirement: number) {
    if (this.netting.ActualBreakingStrength && strengthRequirement && this.netting.ActualBreakingStrength < strengthRequirement) {
      this.nettingStrengthLowerThenRequirements = true;
    } else {
      this.nettingStrengthLowerThenRequirements = false;
    }

    if (!this.netting.ActualBreakingStrength) {
      this.nettingMissingActualBreakingStrength = true;
    } else {
      this.nettingMissingActualBreakingStrength = false;
    }
  }

  async attached() {
    this.validCombinations = this.placementService.getValidCombinations(this.isCombiNet);
    await this.getPlacements();
    this.applyValidation(this.isMorenotNet);
  }

  async createNetting() {
    try {
      if (!(await this.isFormValid())) {
        return;
      }

      this.netting.NetId = this.netId;
      await this.nettingService.post(this.netting);

      this.eventAggregator.publish('nettingListReset', 1);
      this.eventAggregator.publish('nettingFormNewClose', 1);
      this.toastService.showSuccess('netting.created');
      this.netting = new Models.Netting();
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  private async getPlacements() {
    try {
      this.placements = [];
      const allPlacements = await this.placementService.getAllCached();

      if (this.netHasSameNetting && this.netShapeId !== 7 && this.netShapeId !== 12) {
        allPlacements.forEach((x) => {
          // only show AllNet if the net is set to have same netting on all net
          x.IsDeleted = x.Id !== 4 && x.Id !== 14 && x.Id !== 15;
        });
      } else if (this.netShapeId === 12) {
        allPlacements.forEach((x) => {
          x.IsDeleted = x.Name != 'Nottak' && x.Name != 'Sikringsnot';
        });
      } else {
        const validCombinations = this.validCombinations[this.netShapeId];
        allPlacements.forEach((x) => {
          // show values based on valid combination of placements and the nets shape
          x.IsDeleted = validCombinations.indexOf(x.Id) === -1;
        });
      }

      const visiblePlacements = allPlacements.filter((x) => !x.IsDeleted);

      if (!this.netting.PlacementId && visiblePlacements.length === 1) {
        setTimeout(() => {
          this.netting.PlacementId = visiblePlacements[0].Id;
        }, 0);
      }

      this.placements = visiblePlacements;
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  async getNettingTypes() {
    const res = await this.nettingTypeService.getAllCached();
    return res.filter((x) => x.NavisionProductId || x.DeltaProductId);
  }

  changeNettingType(event) {
    if (!event.detail.value || event.detail.value === this.netting.NettingTypeId) {
      return;
    }
    this.netting.NettingTypeId = event.detail.value;
    this.nettingTypeService
      .get(this.netting.NettingTypeId + '?$expand=MeshSize,NavisionProduct')
      .then((res) => {
        this.currentNettingType = res;
        this.netting.MeshSizeId = this.currentNettingType.MeshSizeId;
        this.netting.NylonTypeId = this.currentNettingType.NylonTypeId;
        this.netting.MaterialId = this.currentNettingType.MaterialId;
        this.netting.ThreadTypeId = this.currentNettingType.ThreadTypeId;
        this.netting.Omfar = this.currentNettingType.Omfar;
        this.netting.Solidity = this.currentNettingType.Solidity;
        this.netting.MeshOpening = this.currentNettingType.MeshOpening;

        if (this.currentNettingType.NavisionProduct) {
          this.netting.ActualBreakingStrength = this.currentNettingType.NavisionProduct.MinBreakingStrength;
        }
        if (this.dimensionClassId) {
          if (this.currentNettingType.MeshSize) {
            this.nettingService
              .getNettingStrengthRuleByNetId(this.netId, this.currentNettingType.MeshSize.MeshSizeMm)
              .then((result) => {
                this.netting.MinBreakingStrength = result.StrengthRequirement;
                this.trySetRequirementsWarning(result.StrengthRequirement);
              })
              .catch(() => {
                this.toastService.showError('netting.couldNotCalculateMinBreakingStrengthSpecifyManually');
              });
          }
        }
      })
      .catch((err) => this.errorService.handleError(err));
  }

  private changeMeshSize(event) {
    if (!event.detail.value || event.detail.value === this.netting.MeshSizeId) {
      return;
    }
    this.netting.MeshSizeId = event.detail.value;

    if (this.dimensionClassId && this.netting.MeshSizeId) {
      void this.meshSizeService.getAllCached().then((meshSizes) => {
        const meshSize = meshSizes.find((x) => x.Id == this.netting.MeshSizeId);
        if (meshSize) {
          this.nettingService
            .getNettingStrengthRuleByNetId(this.netId, meshSize.MeshSizeMm)
            .then((result) => {
              this.netting.MinBreakingStrength = result.StrengthRequirement;
              this.trySetRequirementsWarning(result.StrengthRequirement);
            })
            .catch(() => {
              this.toastService.showError('netting.couldNotCalculateMinBreakingStrengthSpecifyManually');
            });
        }
      });
    }
  }

  cancel() {
    this.eventAggregator.publish('nettingFormNewClose', 1);
  }
}
